import Cookies from 'js-cookie';
import React, { useEffect, useContext, useState } from 'react';
import { ProSidebar, Menu, MenuItem, SubMenu, SidebarHeader, SidebarFooter, SidebarContent } from 'react-pro-sidebar';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactTooltip from 'react-tooltip';

// PackageJson
import packageJson from '../../../package.json';

// Assets
import AddProcessIcon from '../../assets/svg/addProcess.jsx';
import CalendarIcon from '../../assets/svg/calendarIcon.jsx';
import CameraVideo from '../../assets/svg/cameraVideo.jsx';
import CitizenAttention from '../../assets/svg/citizenAttention.jsx';
import ExternalSources from '../../assets/svg/externalSources.jsx';
import Group from '../../assets/svg/groups.jsx';
import Hamburguer from '../../assets/svg/hamburguer.jsx';
import Logout from '../../assets/svg/logout.jsx';
import MgGlass from '../../assets/svg/magnifyingGlass.jsx';
import ConfigTask from '../../assets/svg/processContent/configTask.jsx';
import Process from '../../assets/svg/processIcon.jsx';
import Report from '../../assets/svg/report.jsx';
import Roles from '../../assets/svg/roles.jsx';
import Sequence from '../../assets/svg/sequence.jsx';
import Templete from '../../assets/svg/templateIcon.jsx';
import Users from '../../assets/svg/users.jsx';

// Context
import { ContentProcessContext } from '../../context/ContentProcessContext.jsx';

// I18n
import i18n from '../../i18n/es.json';

// Services
import { completeLogoutService } from '../../services/Auth';
import {
  getCreationProcesses,
  getMonthlyManagementProcess,
  getMonthlyManagementProcessByArea,
  getProcessReportsMunicipality,
  getReassignProcesses,
} from '../../services/Process';

// Constants
import { PROFILE_ID, SIDEBAR_STROKE } from '../../utils/constants';

// Components
import MenuItemWithTooltip from './MenuItemWithTooltip.jsx';

const Sidebar = ({ collapsed, rtl, toggled, handleToggleSidebar, handleCollapsedChange }) => {
  const history = useHistory();
  const auth = JSON.parse(Cookies.get('auth'));
  const profile = auth.profileID;
  const [shouldShowMenuItemMonthly, setShouldShowMenuItemMonthly] = useState(true);
  const [shouldShowMenuItemMonthlyArea, setShouldShowMenuItemMonthlyArea] = useState(true);
  const [shouldShowMenuItemReportsMunicipality, setShouldShowMenuItemReportsMunicipality] = useState(true);

  // Context
  const { creationProcess, setCreationProcess, setReassignProcess } = useContext(ContentProcessContext);

  // Verifica si el usuario posee permisos para acceder al reporte de gestion mensual (particular del Municipio de Olavarria)
  const checkMonthlyManagementPermission = async () => {
    try {
      const response = await getMonthlyManagementProcess();
      setShouldShowMenuItemMonthly(response.data.result.length > 0);
    } catch (error) {
      setShouldShowMenuItemMonthly(false);
    }
  };

  // Verifica si el usuario posee permisos para acceder al reporte de gestion mensual por área(particular del Municipio de Olavarria)
  const checkMonthlyManagementByAreaPermission = async () => {
    try {
      const response = await getMonthlyManagementProcessByArea();
      setShouldShowMenuItemMonthlyArea(response.data.result.length > 0);
    } catch (error) {
      setShouldShowMenuItemMonthlyArea(false);
    }
  };

  // Verifica si el usuario posee permisos para acceder al reporte de gestion mensual por área(particular del Municipio de Olavarria)
  const checkProcessReportsMunicipalityPermission = async () => {
    try {
      const response = await getProcessReportsMunicipality();
      setShouldShowMenuItemReportsMunicipality(response.data.result.length > 0);
    } catch (error) {
      setShouldShowMenuItemReportsMunicipality(false);
    }
  };

  const getProcesses = async () => {
    try {
      if (auth.profileID === PROFILE_ID.STATE_OPERATOR) {
        const creationProcesses = await getCreationProcesses();
        setCreationProcess(creationProcesses);
        const reassignProcess = await getReassignProcesses();
        setReassignProcess(reassignProcess.data.result);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ------------------------------------------- //
  // ---------- Side Effects Handlers ---------- //
  // ------------------------------------------- //

  useEffect(() => {
    window.addEventListener('resize', (window) => {
      if (window.target.outerWidth < 1400) {
        handleCollapsedChange(true);
      }
    });
    getProcesses();
  }, []);

  useEffect(() => {
    checkMonthlyManagementPermission();
    checkMonthlyManagementByAreaPermission();
    checkProcessReportsMunicipalityPermission();
  }, []);

  // ------------------------------------------- //
  // -------------- Action Handlers ------------ //
  // ------------------------------------------- //

  const onChangeView = (href, process) => {
    try {
      history.push({
        pathname: href,
        state: process,
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const logoutHandler = async () => {
    completeLogoutService();
  };

  // ------------------------------------------- //
  // ---------------- Components --------------- //
  // ------------------------------------------- //

  const contentAdmin = () => {
    return (
      <SidebarContent>
        <Menu iconShape="circle">
          <SubMenu title={i18n.sidebar.users} icon={<Users />}>
            <MenuItem onClick={() => onChangeView('/addUser')}>{i18n.sidebar.addUsers}</MenuItem>
            <hr className="hr-sidebar" />
            <MenuItem onClick={() => onChangeView('/listUsers')}>{i18n.sidebar.adminUsers}</MenuItem>
          </SubMenu>
        </Menu>
        <Menu iconShape="circle">
          <SubMenu title={i18n.sidebar.groups} icon={<Group />}>
            <MenuItem onClick={() => onChangeView('/addGroup')}>{i18n.sidebar.addGroups}</MenuItem>
            <hr className="hr-sidebar" />
            <MenuItem onClick={() => onChangeView('/listGroup')}>{i18n.sidebar.adminGroups}</MenuItem>
          </SubMenu>
        </Menu>
        <Menu iconShape="circle">
          <SubMenu title={i18n.sidebar.roles} icon={<Roles />}>
            <MenuItem onClick={() => onChangeView('/addRole')}>{i18n.sidebar.addRoles}</MenuItem>
            <hr className="hr-sidebar" />
            <MenuItem onClick={() => onChangeView('/listRoles')}>{i18n.sidebar.adminRoles}</MenuItem>
          </SubMenu>
        </Menu>
        <Menu iconShape="circle">
          <SubMenu title={i18n.sidebar.municipality} icon={<CitizenAttention />}>
            <MenuItem onClick={() => onChangeView('/adminServiceTypes')}>{i18n.sidebar.serviceTypes}</MenuItem>
            <hr className="hr-sidebar" />
            <MenuItem onClick={() => onChangeView('/reportedSituation')}>{i18n.sidebar.reportedSituation}</MenuItem>
            <hr className="hr-sidebar" />
            <MenuItem onClick={() => onChangeView('/catalogs')}>{i18n.sidebar.catalogs}</MenuItem>
          </SubMenu>
        </Menu>
      </SidebarContent>
    );
  };

  const contentProcessAdmin = () => {
    return (
      <SidebarContent>
        <Menu iconShape="circle">
          <SubMenu title={i18n.sidebar.process} icon={<Process stroke={SIDEBAR_STROKE} />}>
            <MenuItemWithTooltip onClick={() => onChangeView('/process')}>
              {i18n.sidebar.adminProcess}
            </MenuItemWithTooltip>
            <hr className="hr-sidebar" />
            <MenuItemWithTooltip onClick={() => onChangeView('/listProcessInstances')}>
              {i18n.sidebar.listProcessInstances}
            </MenuItemWithTooltip>
            <hr className="hr-sidebar" />
            <MenuItemWithTooltip onClick={() => onChangeView('/taskReassignment')}>
              {i18n.sidebar.reassignTasks}
            </MenuItemWithTooltip>
          </SubMenu>
        </Menu>
        <Menu iconShape="circle">
          <MenuItem icon={<Sequence />} onClick={() => onChangeView('/adminSequence')}>
            {i18n.sidebar.sequence}
          </MenuItem>
        </Menu>
        <Menu iconShape="circle">
          <MenuItem icon={<Templete />} onClick={() => onChangeView('/adminTemplates')}>
            {i18n.sidebar.template}
          </MenuItem>
        </Menu>
        <Menu iconShape="circle">
          <MenuItem icon={<ExternalSources />} onClick={() => onChangeView('/externalSources')}>
            {i18n.sidebar.externalSources}
          </MenuItem>
        </Menu>
        <Menu iconShape="circle">
          <MenuItem icon={<CalendarIcon />} onClick={() => onChangeView('/calendar')}>
            {i18n.sidebar.calendar}
          </MenuItem>
        </Menu>
      </SidebarContent>
    );
  };

  const contentOperator = () => {
    return (
      <SidebarContent>
        <Menu iconShape="circle">
          <SubMenu title={i18n.sidebar.newProcess} icon={<AddProcessIcon />}>
            {creationProcess?.data?.result?.map((process) => {
              return (
                <div key={process.id}>
                  <ReactTooltip
                    effect="solid"
                    place="right"
                    backgroundColor="#60a0ff"
                    className="tooltip"
                    id={process.name}
                  >
                    {process.name}
                  </ReactTooltip>
                  <MenuItem onClick={() => onChangeView('/creationForm', process)} data-tip data-for={process.name}>
                    {process.name}
                  </MenuItem>
                  <hr className="hr-sidebar" />
                </div>
              );
            })}
          </SubMenu>
        </Menu>
        <Menu iconShape="circle">
          <MenuItem icon={<ConfigTask />} onClick={() => onChangeView('/myTasks')}>
            {i18n.sidebar.myTasks}
          </MenuItem>
        </Menu>
        <Menu>
          <MenuItem icon={<MgGlass stroke="#eee" />} onClick={() => onChangeView('/processSearch')}>
            {i18n.sidebar.searches}
          </MenuItem>
        </Menu>
        <Menu>
          <MenuItem icon={<CameraVideo stroke="#eee" />} onClick={() => onChangeView('/monitoringVariable')}>
            {i18n.viewTitles.monitoring}
          </MenuItem>
        </Menu>
        <Menu iconShape="circel">
          <SubMenu title={i18n.sidebar.reporting.name} icon={<Report />}>
            <div key={i18n.sidebar.reporting.pendingTasks}>
              <ReactTooltip
                effect="solid"
                place="right"
                backgroundColor="#60a0ff"
                className="tooltip"
                id={i18n.sidebar.reporting.pendingTasks}
              >
                {i18n.sidebar.reporting.pendingTasks}
              </ReactTooltip>
              <MenuItem
                onClick={() => onChangeView('/reportPendingTask')}
                data-tip
                data-for={i18n.sidebar.reporting.pendingTasks}
              >
                {i18n.sidebar.reporting.pendingTasks}
              </MenuItem>
              <hr className="hr-sidebar" />
            </div>
            <div key={i18n.sidebar.reporting.processHistory}>
              <ReactTooltip
                effect="solid"
                place="right"
                backgroundColor="#60a0ff"
                className="tooltip"
                id={i18n.sidebar.reporting.processHistory}
              >
                {i18n.sidebar.reporting.processHistory}
              </ReactTooltip>
              <MenuItem
                onClick={() => onChangeView('/reportProcessHistory')}
                data-tip
                data-for={i18n.sidebar.reporting.processHistory}
              >
                {i18n.sidebar.reporting.processHistory}
              </MenuItem>
              <hr className="hr-sidebar" />
            </div>
            <div key={i18n.sidebar.reporting.monthlyManagement}>
              <ReactTooltip
                effect="solid"
                place="right"
                backgroundColor="#60a0ff"
                className="tooltip"
                id={i18n.sidebar.reporting.monthlyManagement}
              >
                {i18n.sidebar.reporting.monthlyManagement}
              </ReactTooltip>
              {shouldShowMenuItemMonthly && (
                <>
                  <MenuItem
                    onClick={() => onChangeView('/reportMonthlyManagement')}
                    data-tip
                    data-for={i18n.sidebar.reporting.monthlyManagement}
                  >
                    {i18n.sidebar.reporting.monthlyManagement}
                  </MenuItem>
                  <hr className="hr-sidebar" />
                </>
              )}
            </div>
            <div key={i18n.sidebar.reporting.monthlyManagementByArea}>
              <ReactTooltip
                effect="solid"
                place="right"
                backgroundColor="#60a0ff"
                className="tooltip"
                id={i18n.sidebar.reporting.monthlyManagementByArea}
              >
                {i18n.sidebar.reporting.monthlyManagementByArea}
              </ReactTooltip>
              {shouldShowMenuItemMonthlyArea && (
                <>
                  <MenuItem
                    onClick={() => onChangeView('/reportMonthlyManagementByArea')}
                    data-tip
                    data-for={i18n.sidebar.reporting.monthlyManagementByArea}
                  >
                    {i18n.sidebar.reporting.monthlyManagementByArea}
                  </MenuItem>
                  <hr className="hr-sidebar" />
                </>
              )}
            </div>
            <div key={i18n.sidebar.reporting.reportsMunicipality}>
              <ReactTooltip
                effect="solid"
                place="right"
                backgroundColor="#60a0ff"
                className="tooltip"
                id={i18n.sidebar.reporting.reportsMunicipality}
              >
                {i18n.sidebar.reporting.reportsMunicipality}
              </ReactTooltip>
              {shouldShowMenuItemReportsMunicipality && (
                <MenuItem
                  onClick={() => onChangeView('/reportReportsMunicipality')}
                  data-tip
                  data-for={i18n.sidebar.reporting.reportsMunicipality}
                >
                  {i18n.sidebar.reporting.reportsMunicipality}
                </MenuItem>
              )}
            </div>
          </SubMenu>
        </Menu>
      </SidebarContent>
    );
  };

  // ------------------------------------------- //
  // ----------------- Return ------------------ //
  // ------------------------------------------- //

  return (
    <ProSidebar rtl={rtl} collapsed={collapsed} toggled={toggled} breakPoint="md" onToggle={handleToggleSidebar}>
      <SidebarHeader>
        <div className="container">
          <div className="row col-xl-12 px-2 mt-3 mb-5">
            <div className="container text-nowrap">
              <div className="row" onClick={() => handleCollapsedChange(!collapsed)}>
                <div className="col-12 mt-2">
                  <Hamburguer marginBottom="11.5rem" />
                  <label className="sidebar-title">{i18n.sidebar.title}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SidebarHeader>
      {profile === PROFILE_ID.STATE_PROCESS_ADMIN && contentProcessAdmin()}
      {profile === PROFILE_ID.STATE_ADMIN && contentAdmin()}
      {profile === PROFILE_ID.STATE_OPERATOR && contentOperator()}
      <SidebarFooter>
        <div className="row logout">
          <div className="col-xl-12">
            <div className="container-fluid px-3 ms-1">
              <div className="row text-nowrap" onClick={logoutHandler}>
                <div className="col-12">
                  <Logout />
                  <label className="px-2 sidebar-user-label logout">{i18n.sidebar.footerLogout}</label>
                </div>
              </div>
              <div className="row mt-1 text-end mb-3 sidebar-version">
                <label>
                  {i18n.sidebar.footerVersion} {packageJson.version}
                </label>
              </div>
            </div>
          </div>
        </div>
      </SidebarFooter>
    </ProSidebar>
  );
};

export default Sidebar;
